import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import Logo from '../components/Logo';
import { PasswordInput } from '../components/ui';

export default function Login() {
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { loginWithCredentials } = useAuth();
  const navigate = useNavigate();
  // Admin can close new sign-ups (Site settings). Hidden until confirmed open, so the
  // link never flashes when registration is closed.
  const [registrationOpen, setRegistrationOpen] = useState(false);

  useEffect(() => {
    api.get('/auth/registration-status')
      .then((r) => setRegistrationOpen(r.data?.enabled !== false))
      .catch(() => setRegistrationOpen(true)); // older server without the setting: keep the link
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const userData = await loginWithCredentials(emailOrPhone, password);
      // Default page after any expired trial or paid period: Subscription (activate / renew)
      const isStaff = userData.role === 'ROLE_ADMIN' || userData.role === 'ROLE_SEMI_ADMIN';
      const subscriptionExpired = !isStaff && !userData.subscriptionActive;
      navigate(subscriptionExpired ? '/subscribe' : '/signals', {
        replace: true,
        state: subscriptionExpired ? { reason: 'subscription_expired' } : undefined,
      });
    } catch (err) {
      // No response at all = the request never reached the server (network
      // down, wrong API URL, or blocked by CORS). A 403 without a message is
      // Spring rejecting the app's origin. Neither is a wrong password, so
      // don't tell the user their credentials are invalid.
      let msg;
      if (!err.response) {
        msg = 'Cannot reach the server. Check your internet connection and try again.';
      } else if (err.response.status === 403 && !err.response.data?.message) {
        msg = 'Server refused the request (403). The app origin may not be allowed by the server.';
      } else {
        msg = err.response.data?.message || 'Invalid credentials. Please try again.';
      }
      // Deactivated accounts: surface support email
      const lower = String(msg).toLowerCase();
      if (
        lower.includes('contact admin') ||
        lower.includes('deactiv') ||
        lower.includes('disabled') ||
        lower.includes('inactive') ||
        lower.includes('not active')
      ) {
        setError(
          msg.includes('stockxignal@gmail.com')
            ? msg
            : `${msg} Contact stockxignal@gmail.com`
        );
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-[calc(100vh-3.5rem)] flex-col items-center justify-center py-8 sm:py-12">
      <div className="mb-6 flex flex-col items-center text-center">
        <div className="flex items-center gap-2.5">
          <Logo size={30} />
          <span className="font-display text-xl font-semibold tracking-tight text-slate-900">StockXignal</span>
        </div>
        <p className="mt-2 text-sm text-slate-500">Your signals are waiting</p>
      </div>

      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white px-6 py-7 shadow-card">
        <div>
          <div>
            <h1 className="text-center font-display text-lg font-semibold tracking-tight text-slate-900">Sign in</h1>

            {error && (
              <div className="mt-5 rounded-xl border border-rose-100 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700">
                {error.includes('stockxignal@gmail.com') ? (
                  <>
                    {error.split('stockxignal@gmail.com')[0]}
                    <a
                      href="mailto:stockxignal@gmail.com"
                      className="font-semibold underline underline-offset-2 hover:text-rose-900"
                    >
                      stockxignal@gmail.com
                    </a>
                    {error.split('stockxignal@gmail.com')[1] || ''}
                  </>
                ) : (
                  error
                )}
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-5 space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-600">Email or phone</label>
                <input
                  type="text"
                  inputMode="email"
                  autoComplete="username"
                  value={emailOrPhone}
                  onChange={(e) => setEmailOrPhone(e.target.value)}
                  required
                  className="fs-input"
                  placeholder="you@email.com or +1…"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-600">Password</label>
                <PasswordInput
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="fs-input"
                  placeholder="••••••••"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="fs-btn-primary w-full touch-manipulation py-2.5 text-sm"
              >
                {loading ? 'Signing in…' : 'Sign in'}
              </button>
            </form>

            <p className="mt-4 text-center text-sm">
              <Link to="/forgot-password" className="text-slate-600 underline hover:text-slate-900">
                Forgot password?
              </Link>
            </p>

            {registrationOpen && (
              <p className="mt-5 text-center text-sm text-slate-500">
                New here?{' '}
                <Link to="/register" className="font-medium text-slate-900 underline-offset-2 hover:underline">
                  Create an account
                </Link>
              </p>
            )}

            <p className="mt-6 text-center text-[11px] leading-relaxed text-slate-400">
              By signing in you agree to our{' '}
              <Link to="/legal" className="underline text-slate-500 hover:text-slate-700">
                Terms &amp; Legal Disclaimer
              </Link>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
